/**
 * Emulate mode — View as (piece 12, decision 0188). 🔒 The person emulated is
 * seen and worked as exactly. 🔨 While emulating, every permission check
 * evaluates the written rules against the emulated employment instead of
 * returning true, so the mode is what turns the model on before enforcement
 * does.
 */
import type { EnforcementMode, Viewer } from '../permissions/viewer';

export class EmulationError extends Error {}

export interface EmulationTarget {
  employmentId: string;
  personId: string;
  personName: string;
  isSuperAdmin: boolean;
}

export interface EmulationState {
  /** The real person, whose avatar the header keeps throughout. */
  realPersonId: string;
  target: EmulationTarget | null;
}

export const NOT_EMULATING: EmulationState = { realPersonId: '', target: null };

/** The mode the API reads: sandbox until somebody emulates, and emulate for the whole session after. */
export function modeFor(state: EmulationState, base: EnforcementMode): EnforcementMode {
  if (base === 'enforced') return 'enforced';
  return state.target ? 'emulate' : base;
}

/**
 * 🔨 A super admin cannot emulate another super admin, and cannot change their
 * own roles while emulating.
 */
export function assertMayEmulate(actor: { personId: string; isSuperAdmin: boolean }, target: EmulationTarget): void {
  if (!actor.isSuperAdmin) throw new EmulationError('only a super admin emulates another identity');
  if (target.isSuperAdmin) throw new EmulationError('a super admin cannot emulate another super admin');
  if (target.personId === actor.personId) throw new EmulationError('there is nothing to see by emulating yourself');
}

/**
 * 🔨 The picker's list, `GET shell/emulation-targets`, answers a super admin's
 * session alone once enforcement is on, judged on the viewer the request
 * resolves to; in sandbox mode the seeded developer reads it (piece 12;
 * decision 0349). 💰 The list answered every caller until 2026-09-12, with
 * the refusal on the write alone (found while building, 2026-09-09).
 */
export function mayListEmulationTargets(mode: EnforcementMode, viewerIsSuperAdmin: boolean): boolean {
  if (mode === 'sandbox') return true;
  return viewerIsSuperAdmin;
}

export function assertRoleChangeAllowed(state: EmulationState, subjectPersonId: string): void {
  if (state.target && subjectPersonId === state.realPersonId) {
    throw new EmulationError('a role change on your own account is refused while you are viewing as somebody else');
  }
}

/** The banner's own sentence, so the shell and the suite read one wording. */
export function bannerText(state: EmulationState): string | null {
  return state.target ? `Viewing as ${state.target.personName}` : null;
}

/**
 * 🔨 Every write while emulating records both identities: the real person as the
 * actor, the emulated one as who it was done on behalf of. Starting and ending
 * an emulation are audit events of their own.
 */
export interface DualIdentity {
  actorPersonId: string;
  onBehalfOfPersonId: string | null;
}

export function identitiesFor(state: EmulationState): DualIdentity {
  return { actorPersonId: state.realPersonId, onBehalfOfPersonId: state.target?.personId ?? null };
}

export const EMULATION_AUDIT_ACTIONS = { started: 'emulation.started', ended: 'emulation.ended' } as const;

/**
 * The viewer a check runs against: the emulated employment's facts while
 * emulating, and the real viewer otherwise.
 */
export function viewerFor(real: Viewer, emulated: Viewer | null, state: EmulationState): Viewer {
  return state.target && emulated ? emulated : real;
}

/**
 * 🔨 While no session exists the shell holds the emulated employment and sends
 * it with each request. A request carrying a session resolves the value
 * server-side instead, and refuses a client-supplied one.
 */
/**
 * 💰 The shell sent `x-bms2-emulating-employment` while the API read
 * `x-bms2-emulate`, the name piece 11 declares, so View as changed the banner
 * and nothing behind it: every request still resolved to the developer (found
 * while building, 2026-09-09). One constant, declared here and read by both.
 */
export const EMULATION_HEADER = 'x-bms2-emulate';

export function acceptsClientEmulation(hasSession: boolean): boolean {
  return !hasSession;
}
